import { CheckCircle2, ChevronLeft, HeartPulse } from "lucide-react";
import { useMarketingT } from "@/hooks/useMarketingT";

type MetricTone = "positive" | "warning" | "neutral";

interface Metric {
  ar: string;
  en: string;
  fr: string;
  value: string;
  tone: MetricTone;
}

const toneClasses: Record<MetricTone, string> = {
  positive: "border-success/45 text-success",
  warning: "border-warning/55 text-warning",
  neutral: "border-border text-foreground",
};

export function LocalizedFinancialHealth() {
  const { t, lang } = useMarketingT();
  const isRtl = lang === "ar";

  const metrics: Metric[] = [
    { ar: "مدة كفاية النقد", en: "Cash runway", fr: "Autonomie de trésorerie", value: t("6.4 أشهر", "6.4 months", "6,4 mois"), tone: "positive" }, // i18n-allow: tri-locale data / Urdu-only demo
    { ar: "النسبة المتداولة", en: "Current ratio", fr: "Ratio de liquidité générale", value: "2.26", tone: "positive" }, // i18n-allow: tri-locale data / Urdu-only demo
    { ar: "النسبة السريعة", en: "Quick ratio", fr: "Ratio de liquidité réduite", value: "1.78", tone: "warning" }, // i18n-allow: tri-locale data / Urdu-only demo
    { ar: "أيام التحصيل", en: "Days sales outstanding", fr: "Délai moyen de recouvrement", value: t("24.6 يوم", "24.6 days", "24,6 jours"), tone: "neutral" }, // i18n-allow: tri-locale data / Urdu-only demo
    { ar: "أيام المخزون", en: "Inventory days", fr: "Durée de stockage", value: t("38.4 يوم", "38.4 days", "38,4 jours"), tone: "warning" }, // i18n-allow: tri-locale data / Urdu-only demo
    { ar: "أيام السداد", en: "Days payable outstanding", fr: "Délai moyen de paiement", value: t("17.3 يوم", "17.3 days", "17,3 jours"), tone: "warning" }, // i18n-allow: tri-locale data / Urdu-only demo
    { ar: "دورة تحويل النقد", en: "Cash conversion cycle", fr: "Cycle de conversion de trésorerie", value: t("45.7 يوم", "45.7 days", "45,7 jours"), tone: "neutral" }, // i18n-allow: tri-locale data / Urdu-only demo
    { ar: "هامش الربح الإجمالي", en: "Gross profit margin", fr: "Marge brute", value: "42.5%", tone: "positive" }, // i18n-allow: tri-locale data / Urdu-only demo
    { ar: "هامش التشغيل", en: "Operating margin", fr: "Marge d’exploitation", value: "18.6%", tone: "positive" }, // i18n-allow: tri-locale data / Urdu-only demo
    { ar: "هامش صافي الربح", en: "Net profit margin", fr: "Marge nette", value: "13.2%", tone: "positive" }, // i18n-allow: tri-locale data / Urdu-only demo
  ];

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="overflow-hidden rounded-lg border border-border bg-card p-4 shadow-lg shadow-primary/10 sm:p-6"
      aria-label={t(
        "ملخص صحة الشركة والتحليل المالي في سيدا",
        "SIDA company health and financial analysis summary",
        "Résumé de la santé de l’entreprise et de l’analyse financière dans SIDA",
      )}
    >
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground sm:text-sm">
        <HeartPulse className="h-4 w-4 text-primary" aria-hidden="true" />
        <span>{t("ملخص صحة الشركة", "Company health summary", "Santé de l’entreprise")}</span>
      </div>

      <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-success/45 bg-success/5 px-4 py-3">
        <div>
          <p className="text-sm font-bold text-success sm:text-base">
            {t("صحة مالية جيدة", "Good financial health", "Bonne santé financière")}
          </p>
          <p className="mt-1 text-[11px] text-success sm:text-xs">
            {t("تغطية البيانات: 96% · النتيجة: 82/100", "Data coverage: 96% · Score: 82/100", "Couverture des données : 96 % · Score : 82/100")}
          </p>
        </div>
        <CheckCircle2 className="h-6 w-6 shrink-0 text-success" aria-hidden="true" />
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground sm:text-xs">
        {t("مقارنة بآخر 15 يوماً من كل شهر", "Compared with the last 15 days of each month", "Comparaison avec les 15 derniers jours de chaque mois")}
      </p>

      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5 sm:gap-3">
        {metrics.map((metric) => (
          <div key={metric.en} className={`min-h-20 rounded-md border bg-background px-3 py-3 ${toneClasses[metric.tone]}`}>
            <p className="text-[10px] leading-4 text-muted-foreground sm:text-xs">{t(metric.ar, metric.en, metric.fr)}</p>
            <p className="mt-2 text-sm font-bold sm:text-base" dir="ltr">{metric.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-1 text-[10px] text-muted-foreground sm:text-xs">
        <p>{t("• أيام المخزون أعلى من المستهدف، وتحتاج متابعة", "• Inventory days are above target and need attention", "• La durée de stockage dépasse l’objectif et nécessite un suivi")}</p>
        <p>{t("• السيولة تغطي 6.4 أشهر من مصروفات التشغيل", "• Available cash covers 6.4 months of operating expenses", "• La trésorerie couvre 6,4 mois de charges d’exploitation")}</p>
      </div>

      <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground">
        <span>{t("عرض التحليل الكامل", "View full analysis", "Voir l’analyse complète")}</span>
        <ChevronLeft className={`h-3.5 w-3.5 ${isRtl ? "" : "rotate-180"}`} aria-hidden="true" />
      </div>
    </div>
  );
}